import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Flex } from 'ui/acro'
import styled from 'styled-components'
import Text from 'ui/acro/typography/Text'
import CoachMark from './addToCalendar/CoachMark'
import { axios } from 'api'
import moment from 'moment'

const CoachMarkContentContainer = styled(Flex)({
  flexDirection: 'column',
  gap: '16px'
})

const CoachMarkTitle = styled(Text)({
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'Proxima-nova',
  textTransform: 'uppercase'
})

const CoachMarkBody = styled(Text)({
  fontSize: '14px',
  fontFamily: 'Proxima-nova'
})

const CoachMarkButtonWrapper = styled(Flex)({
  fontFamily: 'Proxima-nova',
  fontSize: '14px',
  justifyContent: 'space-between',
  button: {
    border: 'none',
    background: 'none',
    padding: '0',
    color: 'white',
    textDecoration: 'underline'
  }
})

const getItemSteps = (arr = [], hideItems, isNewUser) =>
  arr
    .filter((i) => !hideItems.includes(i.key))
    .filter((i) => (i?.shownTo === 'NEW_USERS' ? isNewUser : true))
    .map((i) => ({
      ...i,
      title: isNewUser ? i.newTitle || i.title : i.title,
      body: isNewUser ? i.newBody || i.body : i.body
    }))
    .sort((a, b) => a.order - b.order)
    .map((i, order) => ({ ...i, order: order + 1 }))

function CoachmarkRenderer({ page = 'classDetails', user = {} }) {
  const createdAt = user?.createdAt
  const username = user?.username
  const [steps, setSteps] = useState([])
  const [originalOrder, setOriginalOrder] = useState({})
  const [, setPushedOrder] = useState([])
  const [nextClicked, setNextClicked] = useState(false)
  const [index, setIndex] = useState(0)
  const [didUserClickClose, setDidUserClickClose] = useState(false)
  const visibleStepsRef = useRef(new Set())
  const [showCoachMark, setShowCoachMark] = useState(false)
  const [isTargetPresent, setIsTargetPresent] = useState(false)

  const manualNavigationRef = useRef(false)
  const debounceRef = useRef(null)
  const hasScrolledRef = useRef(false)
  const step = useMemo(() => steps[index], [steps, index])
  const isNewUser = useMemo(() => {
    if (createdAt) {
      return moment(new Date()).isBefore(moment(createdAt).add(7, 'days'))
    }
    return false
  }, [createdAt])
  const [excludeKeys, setExcludeKeys] = useState({})
  const [interactedSteps, setInteractedSteps] = useState([])

  useEffect(() => {
    const TIMEOUT_MS = 10000

    if (!Object.keys(excludeKeys)?.length) return

    let timeoutId
    let observer = null
    let stopped = false

    const stop = () => {
      if (stopped) return
      stopped = true

      if (observer) observer.disconnect()
      if (timeoutId) clearTimeout(timeoutId)
    }

    const check = () => {
      for (const id in excludeKeys) {
        const el = document.getElementById(excludeKeys[id])
        if (el) {
          let hideItems = []
          try {
            hideItems =
              JSON.parse(localStorage.getItem('read_coachmarks'))?.[username] ||
              []
          } catch {
            hideItems = null
          }

          let data = null
          try {
            data = JSON.parse(localStorage.getItem('all_coachmark_data'))?.[
              username
            ]
          } catch (e) {
            data = null
          }

          let excl = {}
          const filteredData = data?.coachmarks
            ?.filter((i) => i.page === page)
            .filter((i) => {
              if (Boolean(document.getElementById(i.targetId))) {
                return true
              }
              excl = { ...excl, [i.key]: i.targetId }
              return false
            })
          const itemSteps = getItemSteps(filteredData, hideItems, isNewUser)
          setOriginalOrder(
            filteredData?.reduce((acc, item, index) => {
              acc[item.key] = index + 1
              return acc
            }, {})
          )
          if (itemSteps?.length > 0) {
            setExcludeKeys(excl)
            setSteps(itemSteps)
            setShowCoachMark(true)
            setIndex(0)
          }

          stop()
          return true
        }
      }
      return false
    }

    if (check()) return

    observer = new MutationObserver(() => {
      check()
    })

    observer.observe(document.body ?? document.documentElement, {
      childList: true,
      subtree: true
    })

    timeoutId = window.setTimeout(() => {
      stop()
    }, TIMEOUT_MS)

    return () => stop()
  }, [excludeKeys, page, username, isNewUser])

  useEffect(() => {
    const handleCoachmarkData = async (data) => {
      let hideItems = []
      try {
        hideItems =
          JSON.parse(localStorage.getItem('read_coachmarks'))?.[username] || []
      } catch {
        hideItems = []
      }

      const filteredData = data?.coachmarks
        ?.filter((i) => i.page === page)
        .filter((i) => {
          if (Boolean(document.getElementById(i.targetId))) {
            return true
          }
          setExcludeKeys((state) => ({ ...state, [i.key]: i.targetId }))
          return false
        })

      const loadSeen = isNewUser
        ? true
        : Boolean(filteredData?.find((i) => i.shownTo === 'ALL'))

      if (!(hideItems && hideItems?.length > 0) && loadSeen) {
        try {
          const res = await axios.get('/coachmarks/seen')
          hideItems = res?.data?.seenCoachmarks || []
          const cookieData = [...new Set(hideItems)]
          if (cookieData?.length > 0) {
            localStorage.setItem(
              'read_coachmarks',
              JSON.stringify({ [username]: cookieData })
            )
          }
        } catch {}
      }
      const itemSteps = getItemSteps(filteredData, hideItems, isNewUser)
      setOriginalOrder(
        filteredData?.reduce((acc, item, index) => {
          acc[item.key] = index + 1
          return acc
        }, {})
      )
      if (itemSteps?.length > 0) {
        setSteps(itemSteps)
        setIndex(0)
        setShowCoachMark(true)
      }
    }

    const timer = setTimeout(async () => {
      let all_coachmark_data = null
      try {
        all_coachmark_data = JSON.parse(
          localStorage.getItem('all_coachmark_data')
        )?.[username]
      } catch (e) {
        all_coachmark_data = null
      }
      let validVersion = false
      if (all_coachmark_data) {
        const currentVersion = all_coachmark_data?.coachmarks?.[0]?.version
        validVersion =
          window?.REACT_ENV_VARIABLES?.COACHMARKS_USE_LOCAL_STORAGE ===
            'true' &&
          window?.REACT_ENV_VARIABLES?.COACHMARK_VERSION === currentVersion
      }
      if (validVersion) {
        handleCoachmarkData(all_coachmark_data)
      } else {
        axios
          .get('/coachmarks')
          .then((res) => {
            localStorage.setItem(
              'all_coachmark_data',
              JSON.stringify({ [username]: res.data })
            )
            handleCoachmarkData(res.data)
          })
          .catch((err) => {
            console.log(err)
          })
      }
    }, 2000)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isNewUser, username])

  useEffect(() => {
    if (!steps?.length > 0 || didUserClickClose) return

    const observers = []

    steps.forEach((step, i) => {
      const observeStep = () => {
        const el = document.getElementById(step.targetId)
        if (!el) return false

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (manualNavigationRef.current) return

            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              visibleStepsRef.current.add(i)
            } else {
              visibleStepsRef.current.delete(i)
            }

            const visibleIndexes = Array.from(visibleStepsRef.current)

            if (visibleIndexes.length === 0) return

            // filter non interacted steps among list of available steps
            const notInteracted = visibleIndexes.filter(
              (i) => !interactedSteps.includes(steps[i]?.key)
            )
            if (notInteracted.length === 0) return

            // choose step with lowest order
            const nextIndex = notInteracted.reduce((minIdx, currentIdx) =>
              steps[currentIdx]?.order < steps[minIdx]?.order
                ? currentIdx
                : minIdx
            )

            if (debounceRef.current) {
              clearTimeout(debounceRef.current)
            }

            debounceRef.current = setTimeout(() => {
              setIndex((prev) => (prev === nextIndex ? prev : nextIndex))
            }, 200)
          },
          { threshold: 0.6 }
        )

        observer.observe(el)
        observers.push(observer)

        return true
      }

      // retry if element not yet in DOM
      if (!observeStep()) {
        const retry = setInterval(() => {
          if (observeStep()) clearInterval(retry)
        }, 200)
      }
    })

    return () => observers.forEach((o) => o.disconnect())
  }, [steps, didUserClickClose, interactedSteps])

  // this useEffect is to close coachmarks if any when video played
  useEffect(() => {
    const handleClose = () => {
      close()
    }

    window.addEventListener('closeCoachmarks', handleClose)

    return () => {
      window.removeEventListener('closeCoachmarks', handleClose)
    }
  }, [])

  const next = async () => {
    setNextClicked(true)
    const nextIndex = index + 1
    const nextStep = steps[nextIndex]

    let read_coachmarks = []
    try {
      read_coachmarks =
        JSON.parse(localStorage.getItem('read_coachmarks'))?.[username] || []
    } catch {
      read_coachmarks = []
    }

    if (step) {
      localStorage.setItem(
        'read_coachmarks',
        JSON.stringify({
          [username]: [...new Set([...read_coachmarks, step.key])]
        })
      )
      setInteractedSteps((prev) => [...prev, step.key])
    }

    // close coachmark if it is last step
    if (!nextStep) {
      setIndex(-1)
      setDidUserClickClose(true)
      return
    }

    manualNavigationRef.current = true

    const el = document.getElementById(nextStep.targetId)

    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }

    setIndex(nextIndex)

    setTimeout(() => {
      manualNavigationRef.current = false
    }, 600)
  }

  const back = () => {
    if (index <= 0) return

    const prevIndex = index - 1
    const prevStep = steps[prevIndex]

    manualNavigationRef.current = true
    setInteractedSteps((prev) => prev.filter((item) => item !== prevStep.key))

    const el = document.getElementById(prevStep.targetId)

    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }

    setIndex(prevIndex)

    setTimeout(() => {
      manualNavigationRef.current = false
    }, 600)
  }

  const close = () => {
    setIndex(-1)
    setDidUserClickClose(true)
  }

  const handleSeen = () => {
    localStorage.removeItem('abandoned_coachmark_data')
    axios
      .post(`coachmarks/${page}/seen`, {
        exclude_keys: Object.keys(excludeKeys)
      })
      .then((res) => {
        const seenCoachmarks = res?.data?.seenCoachmarks
        if (seenCoachmarks?.length > 0) {
          const cookieData = [...new Set(seenCoachmarks)]
          localStorage.setItem(
            'read_coachmarks',
            JSON.stringify({ [username]: cookieData })
          )
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const handleStep = (stp) => {
    if ((stp === 1 && step?.order === steps.length) || stp === 0) {
      handleSeen()
    }

    let action = 'dismiss'
    if (stp === 1) {
      action = step?.order === steps.length ? 'complete' : 'next'
    } else if (stp === -1) {
      action = 'previous'
    }

    window.dataLayer ||= []
    window.dataLayer.push({
      event: 'coachmark_interaction',
      action,
      coachmark_key: step.key,
      page_name: page,
      step_order: originalOrder[step.key],
      total_steps: Object.keys(originalOrder).length
    })

    switch (stp) {
      case 1:
        next()
        break
      case -1:
        back()
        break
      default:
        close()
        break
    }
  }

  useEffect(() => {
    if (!step?.targetId) return
    const el = document.getElementById(step.targetId)
    if (!el) return

    if (step?.order !== 1) return

    const rect = el.getBoundingClientRect()
    const inView =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)

    if (!inView) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, [step])

  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (!step?.targetId) return
    const el = document.getElementById(step.targetId)
    if (!el) return
    if (step.order !== 1 && !(hasScrolledRef.current || nextClicked)) return

    setPushedOrder((prev) => {
      localStorage.setItem(
        'abandoned_coachmark_data',
        JSON.stringify({
          [username]: {
            event: 'coachmark_interaction',
            action: 'abandoned',
            page_name: page,
            step_order: originalOrder[step.key],
            total_steps: Object.keys(originalOrder).length,
            coachmark_key: step.key
          }
        })
      )
      if (prev.includes(step.order)) {
        return prev
      } else {
        window.dataLayer ||= []
        window.dataLayer.push({
          event: 'coachmark_impression',
          page_name: page,
          step_order: originalOrder[step.key],
          total_steps: Object.keys(originalOrder).length,
          coachmark_key: step.key
        })
        return [...prev, step.order]
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, originalOrder, page, nextClicked])

  useEffect(() => {
    if (!step?.targetId || !showCoachMark) return

    const updatePresence = () => {
      const el = document.getElementById(step.targetId)
      setIsTargetPresent(!!el)
    }

    updatePresence() // initial check

    const observer = new MutationObserver(() => {
      updatePresence()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    return () => observer.disconnect()
  }, [step?.targetId, showCoachMark])

  useEffect(() => {
    if (!isTargetPresent && showCoachMark) {
      // handleStep(1); // or close()
      return
    }
  }, [isTargetPresent, showCoachMark])

  if (!step?.targetId) return null

  const targetEl = document.getElementById(step.targetId)
  if (!isTargetPresent || !showCoachMark) return null

  return (
    <CoachMark
      targetRef={{ current: targetEl }}
      isOpen={true}
      onClose={() => handleStep(0)}
    >
      <CoachMarkContentContainer>
        <CoachMarkTitle>{step.title}</CoachMarkTitle>
        <CoachMarkBody dangerouslySetInnerHTML={{ __html: step?.body }} />
        <CoachMarkButtonWrapper>
          <button onClick={() => handleStep(-1)}>
            {step?.order === 1 ? '' : 'PREVIOUS'}
          </button>
          <button onClick={() => handleStep(1)}>
            {step?.order === steps.length ? 'ALL SET' : 'NEXT'}
          </button>
        </CoachMarkButtonWrapper>
      </CoachMarkContentContainer>
    </CoachMark>
  )
}

export default CoachmarkRenderer
